import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import { FC, ReactNode, createContext, useContext } from 'react'
import { useQuery } from 'react-query'
import { useUserInfo } from 'src/hooks'

const { REACT_APP_AMPLITUDE_EXPERIMENT_CLIENT_ID } = process.env

export type VariantInfo<DataType = unknown> =
    | {
          type: 'value'
          enabled: boolean
          value: string | undefined
      }
    | {
          type: 'config'
          enabled: boolean
          value: string | undefined
          payload: DataType
      }

const newConfigHelper = () => {
    const defaults: Partial<VariantInfo> = {
        value: undefined,
        payload: undefined
    }

    const helper = Object.freeze({
        defaults: () => {
            return Object.assign({}, defaults) as VariantInfo<unknown>
        },
        value: (value: 'control' | 'treatment' | string) => {
            defaults.value = value
            return helper
        },
        payload: (payload: object | undefined) => {
            defaults.payload = payload
            return helper
        }
    })

    return helper
}

export const globals = newConfigHelper()

const AmplitudeExperimentContext = createContext<ExperimentClient | undefined>(
    undefined
)

/**
 * Get the value of an experiment or feature flag
 * Configure experiments in Amplitude Experiment dashboard
 *
 * value is undefined until AE loads user data
 */
export function useAmpliFeatureFlag<PayloadType, DataType = PayloadType>(
    flag: string,
    options: {
        default: 'control' | 'treatment'
        guard?: (payload: unknown) => payload is PayloadType
        // eslint-disable-next-line no-unused-vars
        transform?: (payload: PayloadType) => DataType
    } = { default: 'control' }
): Readonly<VariantInfo<DataType>> {
    const experimentClient = useContext(AmplitudeExperimentContext)

    const result = globals.defaults()
    const variant = experimentClient?.variant(flag, 'pending') || {
        value: options.default,
        payload: undefined
    }
    Object.assign(result, variant)

    const value = String(result.value).toLowerCase()
    result.enabled = /^(yes|on|true|treatment|enable)$/i.test(value)
    result.type = variant.payload !== undefined ? 'config' : 'value'

    if (result.type === 'config') {
        const guard =
            options.guard ||
            ((payload: unknown): payload is PayloadType => true)

        const transform =
            options.transform ||
            ((payload: PayloadType) => payload as unknown as DataType)

        result.payload = guard(result.payload)
            ? transform(result.payload)
            : undefined
    }

    return Object.freeze(result as VariantInfo<DataType>)
}

const amplitudeExperiment = Experiment.initializeWithAmplitudeAnalytics(
    REACT_APP_AMPLITUDE_EXPERIMENT_CLIENT_ID!
)

export const AmplitudeExperimentProvider: FC<{ children: ReactNode }> = ({
    children
}) => {
    const { user } = useUserInfo()

    const { data: experimentClient } = useQuery(
        ['amplitude-experiment-client', user?.id],
        async () =>
            user
                ? amplitudeExperiment.fetch({
                      user_id: user.analyticsUuid,
                      user_properties: {
                          engagementStatus: user.patientEngagementStatus
                      }
                  })
                : undefined,
        {
            enabled: !!user
        }
    )

    return (
        <AmplitudeExperimentContext.Provider value={experimentClient}>
            {children}
        </AmplitudeExperimentContext.Provider>
    )
}
